// Placeholder shown by `LiveJsxBlock` while either of the following is
// true:
//   • The block is `partial` — the closing ``` hasn't arrived yet.
//   • The lazy module registry chunk is still fetching on the first
//     liveTsx of a session (~50–200 ms).
// In both cases the user sees the same "Gerando visualização" card; it
// dissolves into the real React component the moment compileToComponent
// can run.

import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface GeneratingVisualizationProps {
    /**
     * 0..1 fill progress. Drives the six bar buckets left-to-right —
     * each bar represents 1/6 of full. The dot tracer above ignores
     * progress on purpose (it traces the *target* outline so the bars
     * visibly "catch up" to it as ticks land).
     */
    progress?: number;
    bytes?: number;
}

const COMPACT_THRESHOLDS: [number, string][] = [
    [1_000_000_000, 'Gb'],
    [1_000_000, 'Mb'],
    [1_000, 'Kb'],
];

const GeneratingVisualization: React.FC<GeneratingVisualizationProps> = ({ progress = 0, bytes }) => {
    const theme = useTheme();
    const accent = theme.palette.primary.main;
    const accentLight = theme.palette.primary.softBg;
    const ink = theme.palette.text.secondary;

    // Six bars at varied target heights so a full chart reads as real
    // data rather than a uniform shimmer. The dot tracer (below) cycles
    // through these tops at full height. The bars themselves now fill
    // bucket-by-bucket driven by `progress`.
    const start = 10;
    const bars = [
        { x: 0, h: 50, acc: 0 },
        { x: 0, h: 65, acc: 0 },
        { x: 0, h: 60, acc: 0 },
        { x: 0, h: 64, acc: 0 },
        { x: 0, h: 58, acc: 0 },
        { x: 0, h: 70, acc: 0 },
        { x: 0, h: 65, acc: 0 },
    ];
    const width = (220/bars.length)*0.90;
    const dWidth = (220/bars.length)*0.80;
    const baseline = 78;
    const cycleSec = 2;

    let accOverall = 0;
    for (let i = 0; i < bars.length; i++) {
        accOverall += bars[i].h
        bars[i].acc = accOverall
        bars[i].x = start + (i*width);
    }


    function formatCompact(value: number, decimals = 2, addSuffix: boolean = true): string {
        const abs = Math.abs(value);
        for (const [threshold, suffix] of COMPACT_THRESHOLDS) {
            if (abs >= threshold) {
                return (value / threshold).toFixed(decimals) + (addSuffix ? suffix : "");
            }
        }
        return value.toFixed(0) + "b";
    }

    // Map progress to per-bar fill. Bar `i` starts filling at
    // progress = i/N, completes at (i+1)/N. Clamped to [0, 1] so a
    // partial-bar slot stops growing once full and later bars stay
    // empty until their turn.
    const allHeightsSum = bars.reduce((sum, b) => sum + b.h, 0);
    const progressHeight = progress * allHeightsSum;
    const barFill = (i: number) => {
        return bars[i].h - Math.max(bars[i].acc - progressHeight, 0);
    }

    return (
        <Box
            sx={{
                width: 220,
                height: 150,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 0.5,
                py:0.5,
                my: 1,
            }}
        >
            <svg width={220} height={88} viewBox="0 0 220 88" style={{ display: 'block' }}>
                {/* Baseline */}
                <line
                    x1={0}
                    y1={baseline}
                    x2={220}
                    y2={baseline}
                    stroke={theme.palette.divider}
                    strokeWidth={1}
                />
                {/* Bars — progress-driven fill, CSS transition smooths
                    each step between tick arrivals. Browsers that fully
                    support animating SVG presentation attrs via CSS
                    (Chrome/Firefox/Safari 14+) get the transition; the
                    rest still see the bars jump per tick — acceptable
                    fallback since ticks land ~every 100ms anyway. */}
                {bars.map((bar, i) => {
                    const diff = baseline - bar.h;
                    return (
                        <rect
                            key={i}
                            x={bar.x}
                            y={diff}
                            width={dWidth}
                            height={bar.h}
                            fill={accentLight}
                            rx={3}
                            opacity={0.75}
                            style={{ transition: 'height 250ms ease-out, y 250ms ease-out' }}
                        />
                    );
                })}
                {bars.map((bar, i) => {
                    const fill = Math.max(barFill(i),0);
                    const diff = baseline - fill;
                    return (
                        <rect
                            key={i}
                            x={bar.x}
                            y={diff}
                            width={dWidth}
                            height={fill}  
                            fill={accent}
                            rx={3}
                            opacity={0.75}
                            style={{ transition: 'height 250ms ease-out, y 250ms ease-out' }}
                        />
                    );
                })}
                {/* "Drawing pen" — a small dot tracing along the bar tops */}
                <circle r={2.5} fill={accent}>
                    <animate
                        attributeName="cx"
                        values="14; 40; 66; 92; 118; 144; 14"
                        keyTimes="0; 0.16; 0.32; 0.48; 0.64; 0.80; 1"
                        dur={`${cycleSec}s`}
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="cy"
                        values={`${baseline - 32}; ${baseline - 50}; ${baseline - 26}; ${baseline - 44}; ${baseline - 58}; ${baseline - 36}; ${baseline - 32}`}
                        keyTimes="0; 0.16; 0.32; 0.48; 0.64; 0.80; 1"
                        dur={`${cycleSec}s`}
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
            <Stack sx={{textAlign:'center'}}>
                <Stack direction={"row"} gap={0.5} sx={{display:"inline-flex", alignItems:"center", flexWrap:"nowrap"}}>
                    <AutoAwesomeIcon sx={{ fontSize: 13, color: accent }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: ink,
                            fontSize: 11.5,
                            letterSpacing: 0.2,
                            // Subtle pulse on the label so the card feels alive
                            // even between bar cycles.
                            animation: 'gv-pulse 2s ease-in-out infinite',
                            '@keyframes gv-pulse': {
                                '0%, 100%': { opacity: 0.6 },
                                '50%': { opacity: 1 },
                            },
                        }}
                    >
                        Gerando visualização…
                    </Typography>
                </Stack>
                <Typography
                    variant="caption"
                    sx={{
                        color: ink,
                        fontSize: 9.5,
                        letterSpacing: 0.2,
                        // Subtle pulse on the label so the card feels alive
                        // even between bar cycles.
                        animation: 'gv-pulse 2s ease-in-out infinite',
                        '@keyframes gv-pulse': {
                            '0%, 100%': { opacity: 0.6 },
                            '50%': { opacity: 1 },
                        },
                    }}
                >
                    {formatCompact(bytes ?? 0)}
                </Typography>                
            </Stack>
        </Box>
    );
};

export default GeneratingVisualization;
